import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  Flame,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { getSports, getRankings, getSyncStatus, triggerSync } from "../lib/api";

export const Movement = ({ m }) => {
  if (!m) return null;
  if (m.dir === "new")
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[var(--olive-2)]">
        <Sparkles className="w-3.5 h-3.5" /> NEW
      </span>
    );
  if (m.dir === "up")
    return (
      <span className="inline-flex items-center gap-1 text-[13px] font-bold text-[#3f7d4e]">
        <TrendingUp className="w-4 h-4" /> {m.value}
      </span>
    );
  if (m.dir === "down")
    return (
      <span className="inline-flex items-center gap-1 text-[13px] font-bold text-[#b5553f]">
        <TrendingDown className="w-4 h-4" /> {m.value}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--muted)]">
      <Minus className="w-4 h-4" />
    </span>
  );
};

const Rankings = () => {
  const [sports, setSports] = useState([]);
  const [active, setActive] = useState("football");
  const [division, setDivision] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    getSports()
      .then((s) => setSports(s))
      .catch(() => setSports([]));
    getSyncStatus()
      .then(setSync)
      .catch(() => setSync(null));
  }, []);

  const refreshSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
      const [s, st] = await Promise.all([getSports(), getSyncStatus()]);
      setSports(s);
      setSync(st);
      const r = await getRankings(active, division);
      setRows(r);
    } catch (e) {
      // keep existing data on failure
    } finally {
      setSyncing(false);
    }
  };

  const activeSport = sports.find((s) => s.id === active);
  const divisions = activeSport?.divisions || [];

  // reset division when switching sports
  useEffect(() => {
    setDivision(divisions.length ? divisions[0] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sports]);

  useEffect(() => {
    setLoading(true);
    getRankings(active, division)
      .then((r) => setRows(r))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [active, division]);

  return (
    <section id="rankings" className="py-16 sm:py-24 border-t border-[var(--line)]">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-[2px] w-10 bg-[var(--olive)]" />
              <span className="text-xs font-bold tracking-eyebrow text-[var(--muted)] uppercase">
                National Top 20 · By sport
              </span>
            </div>
            <h2 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-[var(--ink)]">
              The{" "}
              <span className="font-serif-display italic font-medium text-[var(--olive-2)]">
                rankings
              </span>
            </h2>
          </div>
          <div className="max-w-sm text-[15px] leading-relaxed text-[var(--muted)]">
            {activeSport && (
              <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)]">
                    {activeSport.season} season ·{" "}
                    {activeSport.source_type === "poll" ? "Established poll" : "Association ranking"}
                  </span>
                </div>
                <div className="mt-1 text-[14px] font-bold text-[var(--ink)]">
                  Source: {activeSport.source}
                </div>
                <div className="text-[13px] text-[var(--muted)]">
                  Updated: {activeSport.updated}
                </div>
                {active === "soccer" && (
                  <div className="mt-3 pt-3 border-t border-[var(--line)] flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--olive-2)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--olive)] animate-pulse" />
                      Auto-syncs from source
                    </span>
                    <button
                      data-testid="rankings-refresh-btn"
                      onClick={refreshSync}
                      disabled={syncing}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--cream)] px-3 py-1.5 text-[12px] font-bold text-[var(--ink)] transition-all hover:border-[var(--olive)] disabled:opacity-60"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                      {syncing ? "Syncing…" : "Refresh"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sport tabs — all sports shown */}
        <div id="sports" className="flex flex-wrap gap-2.5 mb-8">
          {sports.map((s) => (
            <button
              key={s.id}
              data-testid={`sport-tab-${s.id}`}
              onClick={() => setActive(s.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                active === s.id
                  ? "bg-[var(--ink)] text-[var(--cream)]"
                  : "bg-[var(--paper)] text-[var(--ink)] border border-[var(--line)] hover:border-[var(--olive)]"
              }`}
            >
              {s.label}
              <span
                className={`text-[11px] font-medium ${
                  active === s.id ? "text-[var(--olive)]" : "text-[var(--muted)]"
                }`}
              >
                {s.season}
              </span>
            </button>
          ))}
        </div>

        {/* Division toggle (only for sports with divisions) */}
        {divisions.length > 0 && (
          <div className="inline-flex items-center rounded-full border border-[var(--line)] bg-[var(--paper)] p-1 mb-8">
            {divisions.map((d) => (
              <button
                key={d}
                data-testid={`division-${d}`}
                onClick={() => setDivision(d)}
                className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-all ${
                  division === d
                    ? "bg-[var(--olive)] text-white"
                    : "text-[var(--ink)] hover:text-[var(--olive-2)]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        {/* Table card */}
        <div className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] overflow-hidden shadow-[0_1px_0_rgba(30,58,44,0.04)]">
          <div className="hidden sm:grid grid-cols-[60px_1fr_120px_90px_90px_28px] items-center px-6 py-4 border-b border-[var(--line)] bg-[var(--cream-2)]/50">
            <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)]">Rank</span>
            <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)]">Program</span>
            <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)] text-center">Record</span>
            <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)] text-center">Prev</span>
            <span className="text-[11px] font-bold tracking-eyebrow uppercase text-[var(--muted)] text-center">Move</span>
            <span />
          </div>

          {loading && (
            <div className="px-6 py-16 text-center text-[var(--muted)] text-sm">
              Loading rankings…
            </div>
          )}

          {!loading &&
            rows.map((r) => (
              <Link
                to={`/team/${r.slug}`}
                key={r.slug}
                className="grid grid-cols-[46px_1fr_auto] sm:grid-cols-[60px_1fr_120px_90px_90px_28px] items-center px-4 sm:px-6 py-4 border-b border-[var(--line)] last:border-0 transition-colors hover:bg-[var(--cream-2)]/50 group"
              >
                <div className="flex items-center">
                  <span
                    className={`font-serif-display text-2xl sm:text-3xl font-semibold ${
                      r.rank <= 3 ? "text-[var(--olive-2)]" : "text-[var(--ink)]"
                    }`}
                  >
                    {r.rank}
                  </span>
                </div>

                <div className="min-w-0 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] sm:text-[17px] font-bold text-[var(--ink)] truncate group-hover:text-[var(--olive-2)] transition-colors">
                      {r.name}
                    </span>
                    {r.rank === 1 && (
                      <Flame className="w-4 h-4 text-[var(--olive-2)] shrink-0" />
                    )}
                  </div>
                  <span className="block text-[13px] text-[var(--muted)] truncate">
                    {r.location}
                  </span>
                  <div className="sm:hidden flex items-center gap-3 mt-1.5 text-[12px] text-[var(--muted)] font-medium">
                    <span>{r.record}</span>
                    <span className="text-[var(--olive-2)] font-semibold">Prev {r.prev_rank || "NR"}</span>
                  </div>
                </div>

                <span className="hidden sm:block text-center text-[15px] font-semibold text-[var(--ink)] tabular-nums">
                  {r.record}
                </span>
                <span className="hidden sm:block text-center text-[14px] font-semibold text-[var(--olive-2)]" data-testid={`rank-prev-${r.slug}`}>
                  {r.prev_rank || "NR"}
                </span>
                <div className="flex sm:justify-center justify-self-end">
                  <Movement m={r.movement} />
                </div>
                <ChevronRight className="hidden sm:block w-4 h-4 text-[var(--muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
        </div>

        <p className="mt-4 text-[13px] text-[var(--muted)]">
          {activeSport ? `Showing the ${divisions.length ? (division + " ") : ""}${activeSport.label.toLowerCase()} Top 20` : ""} · Click any
          program for its schedule, results and rank history.
        </p>
      </div>
    </section>
  );
};

export default Rankings;
